'use client'
import { useImmutableChannel } from '@/api/hooks'
import { updateChannelChildren, useChannelChildren } from '@/app/(site)/[username]/channel-children'
import { fetchChannelChildren, moveChannel } from '@/app/util/channel-api'
import { EditContext } from '@/context/edit-manager'
import { FlashMessageContext } from '@/context/flash-message'
import UserContext from '@/context/user-context'
import { cn } from '@/util/cn'
import { IChannel } from '@giphy/js-types'
import { DragDropContext, Draggable, Droppable, DropResult } from '@hello-pangea/dnd'
import { useCallback, useContext, useEffect, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import useFetchData from 'ui/src/hooks/use-fetch-data'
import { insertItem } from 'utils'
import Loader from '../../loader'
import { ModalContext } from '../../modal'
import { useChannelMutate } from '../hooks'
import SearchInput from '../search-input'
import { Breadcrumbs } from '../ui'
import ChannelRow from './channel-row'
import NewCollectionRow from './new-collection-row'

type Position = Parameters<typeof moveChannel>[2]

const EditCollections = () => {
    const { user } = useContext(UserContext)
    const { modal, setModalType } = useContext(ModalContext)
    const channelId = modal?.channelId
    const { showMessage } = useContext(FlashMessageContext)
    const id = channelId || user?.channel_id!
    const { data: channel, isLoading } = useImmutableChannel(id)
    const children = useChannelChildren(id, channel?.children)
    const mutateChannel = useChannelMutate()
    const [ids, setIds] = useState<string[]>([])
    const { staffMode } = useContext(EditContext)
    const [updating, setUpdating] = useState(false)
    useEffect(() => {
        if (children) {
            setIds(children.map((c) => c.id.toString()))
        } else {
        }
    }, [children])
    const fetchData = useCallback(
        async (offset: number) => {
            return await fetchChannelChildren(id, '', {
                offset,
                limit: 25,
            })
        },
        [id]
    )

    const { ref, inView } = useInView({ rootMargin: '0px 0px 250px 0px' })
    // use this to fetch but rely on useSWR data
    const { doneFetching, gifs: kids } = useFetchData<IChannel>({
        initialData: children,
        fetchData,
        triggerFetch: inView,
        checkDone: () => children.length === channel?.num_children,
    })
    useEffect(() => {
        updateChannelChildren(id, kids)
    }, [id, kids])
    if (!user) return null
    const onDragEnd = async (result: DropResult) => {
        const { draggableId, source, destination } = result
        const targetIndex = destination?.index
        const originalIndex = source.index
        if (targetIndex !== undefined && targetIndex !== originalIndex) {
            setUpdating(true)
            // remove dragged item
            let newKids = ids.filter((id) => id.toString() !== draggableId)
            // insert dragged item
            newKids = insertItem(newKids, targetIndex, draggableId)
            // update ui synchronously
            setIds(newKids)

            const neighborRight = newKids[targetIndex + 1]
            const position: Position = neighborRight ? 'left' : 'right'
            const neighbor = position === 'right' ? newKids[targetIndex - 1] : neighborRight
            let failed = false
            try {
                const response = await moveChannel(draggableId, neighbor, position)
                failed = !response.ok
            } catch (e) {
                failed = true
            }
            if (failed) {
                // revert
                setIds(ids)
                showMessage({ type: 'error', message: 'There was error updating' })
            }
            setUpdating(false)
            await mutateChannel({
                ...channel!,
                children: newKids.map((id) => children?.find((c) => c.id.toString() === id)!),
            })
            // updateChannelChildren(
            //     id,
            //     newKids.map((id) => children?.find((c) => c.id.toString() === id)!)
            // )
        }
    }

    return (
        <div
            style={{ maxHeight: 'calc(100vh - 200px)' }}
            className={cn('flex flex-col gap-2.5 p-2', updating && 'pointer-events-none opacity-70')}
        >
            {staffMode && (
                <SearchInput
                    onSelect={(channel) => {
                        setModalType({
                            name: 'editCollections',
                            channelId: channel.id,
                            title: <Breadcrumbs channel={channel} />,
                        })
                    }}
                />
            )}
            {channel && <NewCollectionRow className="h-20 w-full p-5" channel={channel} />}
            {isLoading ? (
                <Loader />
            ) : (
                children && (
                    <>
                        <DragDropContext onDragEnd={onDragEnd}>
                            <Droppable droppableId="droppable">
                                {(provided, _snapshot) => (
                                    <div
                                        {...provided.droppableProps}
                                        ref={provided.innerRef}
                                        className={'flex flex-col gap-4'}
                                    >
                                        {ids.map((id, index) => {
                                            const subChannel = children.find((c) => c.id.toString() === id)
                                            if (!subChannel) {
                                                console.error(id, `not found in children:`, children)
                                                return null
                                            }
                                            return (
                                                <Draggable
                                                    key={subChannel.id}
                                                    draggableId={subChannel.id.toString()}
                                                    index={index}
                                                >
                                                    {(provided, snapshot) => (
                                                        <div
                                                            ref={provided.innerRef}
                                                            {...provided.draggableProps}
                                                            className={cn(
                                                                'box-border border-2 border-transparent border-solid',
                                                                snapshot.isDragging && 'border-giphyCharcoal'
                                                            )}
                                                        >
                                                            <ChannelRow
                                                                dragHandleProps={provided.dragHandleProps}
                                                                key={`${subChannel.id}-${index}`}
                                                                channel={subChannel}
                                                            />
                                                        </div>
                                                    )}
                                                </Draggable>
                                            )
                                        })}
                                        {provided.placeholder}
                                    </div>
                                )}
                            </Droppable>
                        </DragDropContext>
                        {!doneFetching && (
                            <div ref={ref}>
                                <Loader />
                            </div>
                        )}
                    </>
                )
            )}
        </div>
    )
}

export default EditCollections
